import { PALETTE } from './palette';

/**
 * A DAY'S VISITS, GROUPED BY THE SHIFT THEY WERE MADE IN.
 *
 * A rep may punch out for lunch and back in for the afternoon, so one attendance day carries several
 * shifts — and "2 shifts" beside a flat list of visits left the reader matching times by hand to see
 * what each shift produced. Each visit is placed by the moment it started counting as a stop (its
 * arrival; its departure-for-the-customer when it never arrived) inside the punch-in → punch-out window
 * of a shift. A visit that falls in NO shift was made while punched out; it gets its own group and is
 * flagged, because work off the clock is exactly what a reviewer needs to notice.
 */
export interface ShiftLike { checkInAt?: string | null; checkOutAt?: string | null; workedMin?: number | null }
export interface VisitShiftGroup<V> {
  /** 1-based shift number; null for the "between shifts" group. */
  no: number | null;
  shift: ShiftLike | null;
  visits: V[];
}

const ms = (x?: string | null) => (x ? new Date(x).getTime() : NaN);
const visitAt = (v: any) => ms(v.checkInAt ?? v.startedAt);

/** Clock skew tolerated between a phone's punch and its visit marks — the server's TIMELINE_SLACK_MS. */
const SLACK_MS = 60_000;

/**
 * The same windows as the server (backend attendance-shifts.ts `shiftIndexOf`), so this grouping and the
 * Visits list's "Shift n of m" can never disagree: a minute of slack either side; a running shift ends
 * now; an EARLIER shift with no punch-out ends when the next one began.
 */
function windows(ordered: ShiftLike[]): { from: number; to: number }[] {
  return ordered.map((s, k) => {
    const out = s.checkOutAt ? ms(s.checkOutAt) : null;
    const nextIn = k + 1 < ordered.length ? ms(ordered[k + 1].checkInAt) : null;
    return { from: ms(s.checkInAt) - SLACK_MS, to: out != null ? out + SLACK_MS : (nextIn ?? Infinity) };
  });
}

/**
 * Null when there is nothing to group (one shift or none) — the caller keeps its plain list then.
 * Shifts are taken in time order.
 */
export function groupVisitsByShift<V>(shifts: ShiftLike[] | null | undefined, visits: V[]): VisitShiftGroup<V>[] | null {
  const list = [...(shifts ?? [])].filter((s) => s.checkInAt).sort((a, b) => ms(a.checkInAt) - ms(b.checkInAt));
  if (list.length < 2) return null;
  const win = windows(list);
  const groups: VisitShiftGroup<V>[] = list.map((s, i) => ({ no: i + 1, shift: s, visits: [] }));
  const between: V[] = [];
  const sorted = [...visits].sort((a, b) => visitAt(a) - visitAt(b));
  for (const v of sorted) {
    const t = visitAt(v);
    const k = win.findIndex((w) => t >= w.from && t <= w.to);
    if (k >= 0) groups[k].visits.push(v); else between.push(v);
  }
  if (between.length) groups.push({ no: null, shift: null, visits: between });
  return groups;
}

/**
 * Each shift wears its own colour, so the eye separates the groups before it reads a word. Amber and
 * red are kept out of the cycle: amber is the "between shifts" warning, red means something is wrong.
 */
const SHIFT_HUES = [PALETTE.sky[500], PALETTE.plum[500], PALETTE.brand[600], PALETTE.earth[500], PALETTE.sky[600]];
export const shiftHue = (no: number | null) => (no == null ? PALETTE.warning[600] : SHIFT_HUES[(no - 1) % SHIFT_HUES.length]);
